import {classificationTexture} from '../classifications';
import shaders from '../shaders';
import { getColorMap } from '../../textures/colormaps';
import {Viewer} from "../main";
import {numberOfClassifications} from "../../../classifications";
import {
  Color,
  DataTexture,
  NearestFilter,
  DoubleSide,
  RGBAFormat,
  RGBFormat,
  ShaderMaterial,
  Uniform,
  Vector2,
  Vector3,
  TextureLoader,
  Texture,
} from "three";
import { getTagTexturePath } from '../../textures/tag-icons';

const packedTextureScale = 0.00025;

export const assignPackedData = (texture, xIndex, yIndex, value) => {
  if (typeof value == "boolean") {
    value = value ? 1.0 : 0.0;
  }

  const valueInt = Math.floor(value / packedTextureScale);
  let data = texture.image.data;
  let height = texture.image.height;
  let width = texture.image.width;
  let channels = texture.image.data.length / (width*height);
  let index = channels*(xIndex + yIndex*width);

  data[index + 0] = (valueInt >> 24) & 255;
  data[index + 1] = (valueInt >> 16) & 255;
  data[index + 2] = (valueInt >> 8) & 255;
  data[index + 3] = (valueInt) & 255;
};

const generateDataTexture = (width: number, height: number, channels, color: Color = new Color(0xffffff)) => {
  let size = width * height;
  let data = new Uint8Array(channels * width * height);

  let r = Math.floor(color.r * 255);
  let g = Math.floor(color.g * 255);
  let b = Math.floor(color.b * 255);

  for (let i = 0; i < size; i++) {
    data[i * channels] = r;
    data[i * channels + 1] = g;
    data[i * channels + 2] = b;

    if (channels === 4) {
      data[i * channels + 3] = 255;
    }
  }

  let format = channels === 3 ? RGBFormat : RGBAFormat;
  let texture = new DataTexture(data, width, height, format);
  texture.minFilter = NearestFilter;
  texture.magFilter = NearestFilter;
  texture.needsUpdate = true;

  return texture;
};

export interface PointCloudMaterialParams {
  minSize: number
  maxSize: number
  heightClamp: Vector2
  intensityClamp: Vector2
  dynamicSize: boolean
  circularPoints: boolean
  colorType: number
  distance: number
  size: number
  colorMap: string
  classification: object
}

/** Shader Materials */

export class BaseMaterial extends ShaderMaterial {
  constructor() {
    super();
  }

  updateValues(parameters) {
    for (const key in parameters) {
      this[key] = parameters[key];
    }
  }

  initProperties() {
    let keys = Object.keys(this.uniforms);
    keys.forEach(key => {
      this.defineProperty(key);
    });
  }

  defineProperty(key) {
    Object.defineProperty(this, key, {
      get: () => {
        return (this.uniforms[key].value);
      },
      set: value => {
        this.uniforms[key].value = value;
      }
    });
  }
}

export class PointCloudMaterial extends BaseMaterial {
    public gpuPicker;
    public size;
    public minSize
    public distance;
    public colorMap;
    public colorType;
    public adaptiveSize;
    public circularPoints;
    public isOctreeCloud: boolean;
    public isPotreeCloud: boolean;
    public origin;
    public screenHeight: number;
    public screenwidth: number;
    public fov: number;
    public octreeSpacing;
    public octreeSize: number;
    public octreeCorner: Vector3;
    public intensityRange: Vector2;
    public rgbMax: number;
    public heightRange: Vector2;
    public timeStamp;
    public classificationVisible;

    public crossSectionWidth: number
    public crossSectionStart: Vector3;
    public crossSectionEnd: Vector3;

    public clipBoxTexture: DataTexture;
    public areaTriangleTexture: DataTexture;
    public visibleNodesTexture: DataTexture;

    public numClippingBoxes = 0;
    public numAreaTriangles = 0;

    constructor(viewer: Viewer, params: PointCloudMaterialParams, gpuPicker) {
      super();

      this.gpuPicker = gpuPicker;

      // Node depth texture for dynamic sizing
      const visibleNodesWidth = 2048;
      const visibleNodesHeight = 1;
      this.visibleNodesTexture = generateDataTexture(
        visibleNodesWidth, visibleNodesHeight, 4);

      // Clip polygon texture (4 attribute + 3x3 coordinates)
      const clipTextureWidth = 1024;
      const clipTextureHeight = 4+9;
      this.clipBoxTexture = generateDataTexture(
        clipTextureWidth, clipTextureHeight, 4);

      // Area/volume texture (1 attribute + 3x3 coordinates)
      const areaTextureWidth = 1024;
      const areaTextureHeight = 1+9;
      this.areaTriangleTexture = generateDataTexture(
        areaTextureWidth, areaTextureHeight, 4);

      // Load initial colormap texture
      const loader = new TextureLoader();
      const image = getColorMap(params.colorMap).image;
      const colorMapTexture = loader.load(image);

      // Generate classification visibility array
      const classificationVisible = generateDataTexture(
        numberOfClassifications, 1, 4);

      let uniforms = {
        gpuPicker: new Uniform(gpuPicker),
        circularPoints: new Uniform(params.circularPoints),
        depth: new Uniform(1.0),
        sphere: new Uniform(viewer.camera.position),
        origin: new Uniform(viewer.camera.position),
        size: new Uniform(params.size),
        minSize: new Uniform(params.minSize),
        maxSize: new Uniform(params.maxSize),
        fov: new Uniform(viewer.camera.fov * Math.PI / 180.0),
        screenHeight: new Uniform(viewer.height),
        screenwidth: new Uniform(viewer.width),
        distance: new Uniform(params.distance),
        octreeSize: new Uniform(0.0),
        octreeSpacing: new Uniform(0.0),
        octreeCorner: new Uniform(viewer.camera.position),
        colorType: new Uniform(params.colorType),
        heightRange: new Uniform(new Vector2(0, 1)),
        heightClamp: new Uniform(params.heightClamp),
        intensityRange: new Uniform(new Vector2(0, 1)),
        intensityClamp: new Uniform(params.intensityClamp),
        rgbMax: new Uniform(0),
        colorMap: new Uniform(colorMapTexture),
        adaptiveSize: new Uniform(params.dynamicSize),
        isOctreeCloud: new Uniform(false),
        isPotreeCloud: new Uniform(false),
        classificationData: new Uniform(classificationTexture),
        classificationVisible: new Uniform(classificationVisible),
        visibleNodes: new Uniform(this.visibleNodesTexture),
        areaTriangleData: new Uniform(this.areaTriangleTexture),
        clipPolygonData: new Uniform(this.clipBoxTexture),
        crossSectionWidth: new Uniform(0),
        crossSectionStart: new Uniform(new Vector3(0, 0, 0)),
        crossSectionEnd: new Uniform(new Vector3(0, 0, 0))
      };

      let defines = {
        NUMBER_OF_CLASSIFICATIONS: numberOfClassifications,
        CLIP_TEXTURE_WIDTH: clipTextureWidth,
        CLIP_TEXTURE_HEIGHT: clipTextureHeight,
        AREA_TEXTURE_WIDTH: areaTextureWidth,
        AREA_TEXTURE_HEIGHT: areaTextureHeight,
        VISIBLE_NODES_WIDTH: visibleNodesWidth,
        PACKED_TEXTURE_SCALE: packedTextureScale
      };

      let fragmentShader = [
        shaders.utilities.frag,
        shaders.gpu_picker.frag,
        shaders.pointcloud.frag
      ].join("\n");

      let vertexShader = [
        shaders.utilities.vert,
        shaders.gpu_picker.vert,
        shaders.texture_packer.vert,
        shaders.pointcloud.vert
      ].join("\n");

      this.setValues({
        defines,
        uniforms,
        vertexShader,
        fragmentShader,
        vertexColors: true,
        transparent: true,
        depthWrite: true
      });

      this.initProperties();
    }

    updateNumClippingBoxes(value) {
      if (value === this.numClippingBoxes) {
        return;
      }

      this.numClippingBoxes = value;

      if (value === 0) {
        delete this.defines.NUM_CLIP_BOXES;
      } else {
        this.defines.NUM_CLIP_BOXES = value;
      }

      this.needsUpdate = true;
    }

    updateNumAreaTriangles(value) {
      if (value === this.numAreaTriangles) {
        return;
      }

      this.numAreaTriangles = value;

      if (value === 0) {
        delete this.defines.NUM_AREA_TRIANGLES;
      } else {
        this.defines.NUM_AREA_TRIANGLES = value;
      }

      this.needsUpdate = true;
    }
}

export class EDLMaterial extends BaseMaterial {
    public screenWidth;
    public screenHeight;
    public edlStrength;
    public far;

    constructor(width, height, near, far, pixelRatio=1.0) {
      super();

      let vertexShader = shaders.edl.vert;
      let fragmentShader = shaders.edl.frag;

      let neighbourCount = 8;
      let neighbours = new Float32Array(neighbourCount * 2);
      for (let i = 0; i < neighbourCount; i++) {
        let value = 2 * i * Math.PI / neighbourCount;
        neighbours[2 * i] = Math.cos(value);
        neighbours[2 * i + 1] = Math.sin(value);
      }

      let defines = {
        NEIGHBOUR_COUNT: neighbourCount,
      };

      let uniforms = {
        screenWidth: new Uniform(width),
        screenHeight: new Uniform(height),
        near: new Uniform(near),
        far: new Uniform(far),
        opacity: new Uniform(1.0),
        edlStrength: new Uniform(1.0),
        radius: new Uniform(1.0 / pixelRatio),
        neighbours: new Uniform(neighbours),
        colorMap: new Uniform(null),
        tDepth: new Uniform(null)
      };

      this.setValues({
        defines,
        uniforms,
        vertexShader,
        fragmentShader,
        transparent: true
      });

      this.initProperties();
    }
}

export class OpacityMaterial extends BaseMaterial {
  constructor() {
    super();

    let vertexShader = shaders.opacity.vert;
    let fragmentShader = shaders.opacity.frag;

    let uniforms = {
      "colorMap": {type: "t", value: null},
      "opacity": {type: "f", value: 1.0}
    };

    this.setValues({
      uniforms,
      vertexShader,
      fragmentShader,
      transparent: true
    });

    this.initProperties();
  }
}

export class ImageMaterial extends BaseMaterial {
  constructor() {
    super();

    let vertexShader = shaders.image.vert;
    let fragmentShader = shaders.image.frag;

    let uniforms = {
      "colorMap": {type: "t",value: null},
      "brightness": {type: "f", value: 0},
      "contrast": {type: "f", value: 0}
    };

    this.setValues({
      uniforms,
      vertexShader,
      fragmentShader,
      side: DoubleSide
    });

    this.initProperties();
  }
}

export class TagMaterial extends BaseMaterial {
  public currentCamRotation;
  public currentCamPosition;
  public windowHeight
  public initialCameraLoading
  public fov

  constructor(texture, gpuPicker) {
    super();

    const texturePath = getTagTexturePath(texture);
    const textureData = new TextureLoader().load(texturePath);

    const fragmentShader = [
      shaders.gpu_picker.frag,
      shaders.utilities.frag,
      shaders.tags.frag
    ].join("\n");

    const vertexShader = [
      shaders.gpu_picker.vert,
      shaders.utilities.vert,
      shaders.tags.vert
    ].join("\n");

    const uniform = {
      scale: new Uniform(1.0),
      gpuPicker: new Uniform(gpuPicker),
      currentCamRotation: new Uniform(new Vector3()),
      currentCamPosition: new Uniform(new Vector3()),
      fov: new Uniform(0),
      initialCameraLoading: new Uniform(false),
      windowHeight: new Uniform(0),
      imageTexture: {
        type: 't',
        value: textureData
      }
    };

    this.setValues({
      uniforms: uniform,
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      side: DoubleSide
    });

    this.initProperties();
  }
}

export class PanoramicMaterial extends BaseMaterial {
  constructor() {
    super();

    let vertexShader = shaders.panoramic.vert;
    let fragmentShader = shaders.panoramic.frag;

    let uniforms = {
      "tEquirect": {
        type: "t",
        value: null
      },
    };

    this.setValues({
      uniforms,
      vertexShader,
      fragmentShader,
      side: DoubleSide
    });

    this.initProperties();
  }

  setTexture(texture) {
    this.uniforms.tEquirect.value = texture;
  }
}

export class MarkerMaterial extends BaseMaterial {
  public currentCamRotation: Vector3;
  public currentCamPosition: Vector3;
  public windowHeight: number;
  public initialCameraLoading: boolean;
  public currentIndex: number;
  public orbitMode: Boolean;
  public fov: number;

  constructor(gpuPicker) {
    super();

    let fragmentShader = [
      shaders.utilities.frag,
      shaders.gpu_picker.frag,
      shaders.markers.frag
    ].join("\n");

    let vertexShader = [
      shaders.utilities.vert,
      shaders.gpu_picker.vert,
      shaders.markers.vert
    ].join("\n");

    let uniforms = {
      scale: new Uniform(0.5),
      gpuPicker: new Uniform(gpuPicker),
      orbitMode: new Uniform(false),
      currentCamRotation: new Uniform(new Vector3()),
      currentCamPosition: new Uniform(new Vector3()),
      fov: new Uniform(0),
      initialCameraLoading: new Uniform(false),
      windowHeight: new Uniform(0),
      currentIndex: new Uniform(0)
    };

    this.setValues({
      uniforms,
      vertexShader,
      fragmentShader,
      side: DoubleSide
    });

    this.initProperties();
  }
}

export class CustomSpriteMaterial extends BaseMaterial {
  public screenHeight;
  public currentCamPosition;
  public fov;
  public map: Texture;
  public size = 1.0;
  public gpuPicker = false;
  public color = new Color(0xffffff);

  constructor(parameters) {
    super();

    this.updateValues(parameters);

    let fragmentShader = [
      shaders.utilities.frag,
      shaders.gpu_picker.frag,
      shaders.sprite.frag
    ].join("\n");

    let vertexShader = [
      shaders.utilities.vert,
      shaders.gpu_picker.vert,
      shaders.sprite.vert
    ].join("\n");

    let uniforms = {
      color: new Uniform(this.color),
      imageTexture: {type: "t", value: this.map},
      gpuPicker: new Uniform(this.gpuPicker),
      size: new Uniform(this.size),
      fov: new Uniform(0),
      screenHeight: new Uniform(0),
    };

    this.setValues({
      uniforms,
      vertexShader,
      fragmentShader,
      side: DoubleSide
    });

    this.initProperties();
  }
}

/** Generic material for gpu picker objects */
export class BasicPickerMaterial extends BaseMaterial {
  constructor(side = DoubleSide) {
    super();

    let vertexShader = [
      shaders.gpu_picker.vert,
      shaders.basic_picker.vert
    ].join("\n");

    let fragmentShader = [
      shaders.gpu_picker.frag,
      shaders.basic_picker.frag
    ].join("\n");

    this.setValues({vertexShader, fragmentShader, side});
    this.initProperties();
  }
}
